import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';

import { isNullOrUndefined } from '@app/util/util';
import { PlaceSelectors } from '@app/entities/place';
import { TranslatePipe } from '@app/translate/TranslatePipe';
import { TravelDetailProvider } from '@shared/flight-itinerary/entities/travel-detail-provider';
import { AirBookAirports } from '@booking/entities/air-booking.response';
import { FlightClassNumericTypes } from '@shared/base-search-bar/entities/flight-class-types';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { FlightCabin, FlightDetailCabinPayload } from '@shared/flight-itinerary/entities/cabin-types';
import { FlightAmenitie } from '@app/entities/flight-results';
import { cabinTypeNameMapper } from '@flight-results/mappers/flights.mapper';
import { FlightDurationSize } from '@design-system/components/molecules/flight-duration/entities/flight-duration.types';

@Component({
  selector: 'app-travel-info',
  templateUrl: './travel-info.component.html',
  styleUrls: ['./travel-info.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TravelInfoComponent {

  @Input() isMobile: boolean = false;
  @Input() airlineCode: string;
  @Input() airlineName: string;
  @Input() flightClass: string;
  @Input() airplane: string;
  @Input() departureDate: string;
  @Input() departureAirport: string;
  @Input() arrivalDate: string;
  @Input() arrivalAirport: string;
  @Input() opAirlineCode: string;
  @Input() opAirlineName: string;
  @Input() number: number;
  @Input() flightTimeInternal: string;
  @Input() travelInfoProvider: TravelDetailProvider = TravelDetailProvider.FLIGHT_RESULTS;
  @Input() fareFamilies: string;
  @Input() airportsTerminals: AirBookAirports[] = [];
  @Input() cabin: FlightClassNumericTypes;
  @Input() source: EVENT_SOURCE | string;
  @Input() isLoadingCabinDetail: boolean = false;
  @Input() cabinAmenitie: FlightAmenitie = null;
  @Input() isLoggedIn: boolean = false;
  @Input() isLoadingFlightCabin: boolean = false;
  @Input() flightCabinSelected: FlightCabin;

  @Output() openCabinDetail: EventEmitter<FlightDetailCabinPayload> = new EventEmitter<FlightDetailCabinPayload>();

  public placeCriteria: string = PlaceSelectors.City;

  public readonly flightDurationSize = FlightDurationSize.MD;

  constructor(
    private translatePipe: TranslatePipe) { }

  get showFlightClass(): boolean {
    return this.travelInfoProvider === TravelDetailProvider.BOOKING_FLIGHT && this.showOnlyInDesktopBooking;
  }

  get noShadow(): boolean {
    return this.showOnlyInDesktopBooking || this.isUserHistory || this.isBus;
  }

  get isBooking(): boolean {
    return this.travelInfoProvider === TravelDetailProvider.BOOKING_FLIGHT ||
      this.travelInfoProvider === TravelDetailProvider.BOOKING_BUSES;
  }

  get isFlightResults(): boolean {
    return this.travelInfoProvider === TravelDetailProvider.FLIGHT_RESULTS;
  }

  get isBusesResults(): boolean {
    return this.travelInfoProvider === TravelDetailProvider.BUS_RESULTS;
  }

  get isUserHistory(): boolean {
    return this.travelInfoProvider === TravelDetailProvider.USER_HISTORY || this.isUserBusHistory;
  }

  get showOnlyInDesktopBooking(): boolean {
    return this.isBooking && !this.isMobile;
  }

  get isOperatedByAnotherAirline(): boolean {
    return (!!this.airlineCode && !!this.opAirlineCode) && this.airlineCode !== this.opAirlineCode;
  }

  get flightKey(): string {
    return `${this.airlineCode}${this.number}`;
  }

  get flightClassText(): string {
    return this.translatePipe.transform(`FLIGHT_RESULTS_MODULE.FLIGHT_CLASSES.${this.labelFlightCabin}`);
  }

  get isBus(): boolean {
    return this.isBusesResults || this.travelInfoProvider === TravelDetailProvider.BOOKING_BUSES || this.isUserBusHistory;
  }

  get isUserBusHistory(): boolean {
    return this.travelInfoProvider === TravelDetailProvider.USER_HISTORY_BUSES;
  }

  get labelFlightCabin(): string {
    return cabinTypeNameMapper(this.cabin);
  }

  get hasCabinAmenities(): boolean {
    return !this.isLoadingCabinDetail && !isNullOrUndefined(this.cabinAmenitie);
  }

  public getTerminalName(airportCode: string): string {
    const airport = this.airportsTerminals.find(terminal => terminal.Code === airportCode);
    return airport ? `${airport.Name}, ${airport.RelatedCityName}` : airportCode;
  }

}
